import { useEffect, useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
}

export function SessionTimeoutModal({
  isOpen,
  remainingSeconds,
  onExtend,
  onLogout,
}: SessionTimeoutModalProps) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(remainingSeconds);

  useEffect(() => {
    setCountdown(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        className="mx-auto w-full max-w-md rounded-xl bg-white shadow-2xl dark:bg-card-dark"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
      >
        <div className="border-b border-gray-200 p-6 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3
                id="session-timeout-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                {t('session.timeout.title', 'Session Timeout Warning')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('session.timeout.subtitle', 'Your session is about to expire')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6 flex items-center justify-center gap-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
            <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div className="text-center">
              <div className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">
                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('session.timeout.remaining', 'Time remaining')}
              </div>
            </div>
          </div>

          <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
            {t(
              'session.timeout.message',
              'Due to inactivity, your session will expire soon. Would you like to continue working?',
            )}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row-reverse">
            <Button onClick={onExtend} className="flex-1">
              {t('session.timeout.extend', 'Continue Session')}
            </Button>
            <Button onClick={onLogout} variant="secondary" className="flex-1">
              {t('session.timeout.logout', 'Log Out Now')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
